import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  Download,
  BookOpen,
  Sprout,
  Building2,
  MapPin,
  Globe,
  ChevronRight,
  FileText,
  Users,
  ClipboardList,
  Bell,
  Upload,
  BarChart3,
  Shield,
  Loader2,
} from "lucide-react";
import { api } from "../App";
import mermaid from "mermaid";
import jsPDF from "jspdf";

// Initialize mermaid
mermaid.initialize({
  startOnLoad: false,
  theme: "base",
  themeVariables: {
    primaryColor: "#059669",
    primaryTextColor: "#fff",
    primaryBorderColor: "#047857",
    lineColor: "#6b7280",
    secondaryColor: "#f0fdf4",
    tertiaryColor: "#ecfdf5",
    fontFamily: "system-ui, sans-serif",
    fontSize: "14px",
  },
  flowchart: {
    htmlLabels: true,
    curve: "basis",
    padding: 15,
    nodeSpacing: 30,
    rankSpacing: 40,
  },
});

// Section icon mapper
const sectionIcons = {
  getting_started: BookOpen,
  login: Shield,
  dashboard: BarChart3,
  new_application: FileText,
  tracking: ClipboardList,
  notifications: Bell,
  language: Globe,
  processing: ClipboardList,
  officials_mgmt: Users,
  import_export: Upload,
  analytics_sla: BarChart3,
  revenue_hierarchy: Users,
  survey_hierarchy: Users,
  office_types: Building2,
  messaging_centre: Bell,
  demo_accounts: Shield,
};

// ─── Standalone Mermaid Diagram Component ───
// Defined outside DocumentationPage to avoid re-creation on re-render
function MermaidDiagram({ definition, diagramKey }) {
  const [svgHtml, setSvgHtml] = useState("");

  useEffect(() => {
    if (!definition) return;
    let cancelled = false;

    const render = async () => {
      try {
        const uniqueId = `mmd-${diagramKey}-${Date.now()}`;
        const { svg } = await mermaid.render(uniqueId, definition);
        if (!cancelled) setSvgHtml(svg);
      } catch (e) {
        console.error("Mermaid render error:", e);
        if (!cancelled) setSvgHtml('<p class="text-red-500 text-sm p-4">Diagram render error</p>');
      }
    };

    const timer = setTimeout(render, 100);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [definition, diagramKey]);

  // Use dangerouslySetInnerHTML to avoid React managing Mermaid's DOM nodes
  return (
    <div className="bg-white border border-stone-200 rounded-xl p-6 overflow-x-auto">
      {svgHtml ? (
        <div
          className="flex justify-center"
          dangerouslySetInnerHTML={{ __html: svgHtml }}
        />
      ) : (
        <div className="flex justify-center min-h-[200px] items-center">
          <div className="text-stone-400 text-sm flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading diagram...
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Helper: build mermaid definition for workflow ───
function buildWorkflowDef(steps, tFn) {
  let def = "graph TD\n";
  steps.forEach((step, i) => {
    const label = tFn(step).replace(/["/]/g, "'");
    const nodeId = `S${i}`;
    const phase = step.phase || "";
    if (phase === "revenue") {
      def += `  ${nodeId}["${label}"]:::revenueNode\n`;
    } else if (phase === "survey") {
      def += `  ${nodeId}["${label}"]:::surveyNode\n`;
    } else {
      def += `  ${nodeId}["${label}"]\n`;
    }
    if (i > 0) def += `  S${i - 1} --> ${nodeId}\n`;
  });
  def += `  classDef revenueNode fill:#059669,stroke:#047857,color:#fff\n`;
  def += `  classDef surveyNode fill:#0284c7,stroke:#0369a1,color:#fff\n`;
  return def;
}

// ─── Helper: build mermaid definition for hierarchy ───
function buildHierarchyDef(roles, tFn) {
  let def = "graph TD\n";
  roles.forEach((role, i) => {
    const label = tFn(role).replace(/["/]/g, "'");
    def += `  R${i}["${label}"]\n`;
  });
  for (let i = 0; i < roles.length - 1; i++) {
    if (roles[i + 1].level > roles[i].level) {
      def += `  R${i} --> R${i + 1}\n`;
    } else if (roles[i + 1].level === roles[i].level) {
      for (let j = i - 1; j >= 0; j--) {
        if (roles[j].level < roles[i + 1].level) {
          def += `  R${j} --> R${i + 1}\n`;
          break;
        }
      }
    }
  }
  return def;
}

export default function DocumentationPage() {
  const navigate = useNavigate();
  const [activeRole, setActiveRole] = useState("farmer");
  const [lang, setLang] = useState("en");
  const [manual, setManual] = useState(null);
  const [workflows, setWorkflows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [activeSection, setActiveSection] = useState(null);

  // Load manual data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [manualRes, workflowRes] = await Promise.all([
          api.get(`/docs/manual/${activeRole}`),
          api.get("/docs/workflows"),
        ]);
        setManual(manualRes.data);
        setWorkflows(workflowRes.data);
        setActiveSection(manualRes.data?.sections?.[0]?.id || null);
      } catch (err) {
        console.error("Failed to load documentation:", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [activeRole]);

  const t = useCallback(
    (obj) => {
      if (!obj) return "";
      return obj[lang] || obj["en"] || "";
    },
    [lang]
  );

  // Diagram components are defined outside - see MermaidDiagram below

  // ─── PDF Generation ───
  const generatePDF = async () => {
    if (!manual) return;
    setGenerating(true);

    try {
      const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 20;
      const contentW = pageW - margin * 2;
      let y = 0;

      const addPage = () => {
        doc.addPage();
        y = margin;
      };

      const checkY = (needed) => {
        if (y + needed > pageH - margin) {
          addPage();
        }
      };

      // ── Cover page ──
      doc.setFillColor(6, 95, 70);
      doc.rect(0, 0, pageW, pageH, "F");

      doc.setTextColor(255, 255, 255);
      doc.setFontSize(32);
      doc.setFont("helvetica", "bold");

      const title = t(manual.title);
      const titleLines = doc.splitTextToSize(title, contentW);
      doc.text(titleLines, pageW / 2, 80, { align: "center" });

      doc.setFontSize(14);
      doc.setFont("helvetica", "normal");
      const subtitle = t(manual.subtitle);
      doc.text(subtitle, pageW / 2, 80 + titleLines.length * 14, { align: "center" });

      doc.setFontSize(11);
      const dateStr = new Date().toLocaleDateString(lang === "kn" ? "kn-IN" : "en-IN", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
      doc.text(dateStr, pageW / 2, pageH - 40, { align: "center" });

      // Footer on cover
      doc.setFontSize(9);
      doc.text("Magadi Aadalat Portal", pageW / 2, pageH - 25, { align: "center" });

      // ── Content pages ──
      manual.sections.forEach((section) => {
        addPage();

        // Section title
        doc.setFillColor(240, 253, 244);
        doc.rect(margin, y - 2, contentW, 12, "F");
        doc.setTextColor(6, 95, 70);
        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        const secTitle = t(section.title);
        doc.text(secTitle, margin + 4, y + 7);
        y += 18;

        // Section content
        if (section.content) {
          doc.setTextColor(51, 51, 51);
          doc.setFontSize(10);
          doc.setFont("helvetica", "normal");
          const contentLines = doc.splitTextToSize(t(section.content), contentW);
          checkY(contentLines.length * 5);
          doc.text(contentLines, margin, y);
          y += contentLines.length * 5 + 6;
        }

        // Steps
        if (section.steps) {
          section.steps.forEach((step, idx) => {
            checkY(20);
            doc.setTextColor(6, 95, 70);
            doc.setFontSize(11);
            doc.setFont("helvetica", "bold");
            doc.text(`Step ${idx + 1}: ${t(step.title)}`, margin + 4, y);
            y += 6;

            doc.setTextColor(51, 51, 51);
            doc.setFontSize(10);
            doc.setFont("helvetica", "normal");
            const descLines = doc.splitTextToSize(t(step.description), contentW - 8);
            checkY(descLines.length * 5);
            doc.text(descLines, margin + 8, y);
            y += descLines.length * 5 + 5;
          });
        }

        // Features
        if (section.features) {
          section.features.forEach((feature) => {
            checkY(8);
            doc.setTextColor(51, 51, 51);
            doc.setFontSize(10);
            doc.setFont("helvetica", "normal");
            const featureText = `  \u2022  ${t(feature)}`;
            const featureLines = doc.splitTextToSize(featureText, contentW - 8);
            doc.text(featureLines, margin + 4, y);
            y += featureLines.length * 5 + 2;
          });
        }

        // Hierarchy
        if (section.hierarchy) {
          section.hierarchy.forEach((role) => {
            checkY(8);
            doc.setTextColor(51, 51, 51);
            doc.setFontSize(10);
            const indent = (role.level - 1) * 10;
            doc.text(
              `${"  ".repeat(role.level)}Level ${role.level}: ${t(role)}`,
              margin + 4 + indent,
              y
            );
            y += 6;
          });
        }
      });

      // ── Workflow pages ──
      if (workflows) {
        const workflowKeys =
          activeRole === "farmer"
            ? ["revenue", "survey", "both"]
            : activeRole === "revenue"
            ? ["revenue", "both"]
            : ["survey", "both"];

        workflowKeys.forEach((key) => {
          const wf = workflows[key];
          if (!wf) return;
          addPage();

          doc.setFillColor(240, 253, 244);
          doc.rect(margin, y - 2, contentW, 12, "F");
          doc.setTextColor(6, 95, 70);
          doc.setFontSize(16);
          doc.setFont("helvetica", "bold");
          const wfTitle =
            lang === "kn"
              ? `ಕಾರ್ಯಪ್ರವಾಹ: ${t(wf.title)}`
              : `Workflow: ${t(wf.title)}`;
          doc.text(wfTitle, margin + 4, y + 7);
          y += 20;

          wf.steps.forEach((step, idx) => {
            checkY(16);
            const isLast = idx === wf.steps.length - 1;

            // Step number circle
            doc.setFillColor(6, 95, 70);
            doc.circle(margin + 8, y, 4, "F");
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(8);
            doc.setFont("helvetica", "bold");
            doc.text(`${idx + 1}`, margin + 8, y + 1.2, { align: "center" });

            // Step label
            doc.setTextColor(51, 51, 51);
            doc.setFontSize(11);
            doc.setFont("helvetica", "normal");
            doc.text(t(step), margin + 18, y + 1);

            if (step.phase) {
              const phaseLabel = step.phase === "revenue" ? 
                (lang === "kn" ? "ಕಂದಾಯ" : "Revenue") : 
                (lang === "kn" ? "ಸರ್ವೇ" : "Survey");
              doc.setTextColor(120, 120, 120);
              doc.setFontSize(8);
              doc.text(`[${phaseLabel}]`, margin + 18 + doc.getTextWidth(t(step)) + 3, y + 1);
            }

            // Connector line
            if (!isLast) {
              doc.setDrawColor(6, 95, 70);
              doc.setLineWidth(0.4);
              doc.line(margin + 8, y + 4, margin + 8, y + 12);
              // Arrow
              doc.setFillColor(6, 95, 70);
              doc.triangle(
                margin + 8, y + 13,
                margin + 6.5, y + 11,
                margin + 9.5, y + 11,
                "F"
              );
            }
            y += 16;
          });
        });
      }

      const roleName = activeRole.charAt(0).toUpperCase() + activeRole.slice(1);
      const fileName = `Magadi_Aadalat_${roleName}_Manual_${lang.toUpperCase()}.pdf`;
      doc.save(fileName);
    } catch (err) {
      console.error("PDF generation error:", err);
    } finally {
      setGenerating(false);
    }
  };

  // ─── Render ───
  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  const currentSection = manual?.sections?.find((s) => s.id === activeSection);

  return (
    <div className="min-h-screen bg-stone-50" data-testid="documentation-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-800 to-emerald-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
                className="text-white hover:bg-emerald-600/50"
                data-testid="back-button"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                {lang === "kn" ? "ಹಿಂದೆ" : "Back"}
              </Button>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
                  <BookOpen className="w-6 h-6" />
                  {lang === "kn" ? "ಬಳಕೆದಾರ ಕೈಪಿಡಿ ಮತ್ತು ಸಹಾಯ" : "User Manual & Help"}
                </h1>
                <p className="text-emerald-100 text-sm mt-0.5">
                  {lang === "kn"
                    ? "ಮಾಗಡಿ ಅದಾಲತ್ ಪೋರ್ಟಲ್ ಮಾರ್ಗದರ್ಶಿ"
                    : "Magadi Aadalat Portal Guide"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Language Toggle */}
              <div className="flex rounded-lg overflow-hidden border border-emerald-500" data-testid="lang-toggle">
                <button
                  onClick={() => setLang("en")}
                  className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                    lang === "en"
                      ? "bg-white text-emerald-800"
                      : "bg-emerald-700 text-emerald-100 hover:bg-emerald-600"
                  }`}
                >
                  EN
                </button>
                <button
                  onClick={() => setLang("kn")}
                  className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                    lang === "kn"
                      ? "bg-white text-emerald-800"
                      : "bg-emerald-700 text-emerald-100 hover:bg-emerald-600"
                  }`}
                >
                  KN
                </button>
              </div>
              {/* Download PDF */}
              <Button
                size="sm"
                onClick={generatePDF}
                disabled={generating}
                className="bg-white text-emerald-800 hover:bg-emerald-50"
                data-testid="download-pdf-btn"
              >
                {generating ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-1" />
                )}
                {lang === "kn" ? "PDF ಡೌನ್‌ಲೋಡ್" : "Download PDF"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Role Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 -mt-4">
        <Tabs
          value={activeRole}
          onValueChange={(v) => {
            setActiveRole(v);
            setActiveSection(null);
          }}
          data-testid="role-tabs"
        >
          <TabsList className="bg-white shadow-md border border-stone-200 p-1 h-auto">
            <TabsTrigger
              value="farmer"
              className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white px-4 py-2"
              data-testid="farmer-tab"
            >
              <Sprout className="w-4 h-4 mr-1.5" />
              {lang === "kn" ? "ರೈತ" : "Farmer"}
            </TabsTrigger>
            <TabsTrigger
              value="revenue"
              className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white px-4 py-2"
              data-testid="revenue-tab"
            >
              <Building2 className="w-4 h-4 mr-1.5" />
              {lang === "kn" ? "ಕಂದಾಯ" : "Revenue"}
            </TabsTrigger>
            <TabsTrigger
              value="survey"
              className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white px-4 py-2"
              data-testid="survey-tab"
            >
              <MapPin className="w-4 h-4 mr-1.5" />
              {lang === "kn" ? "ಸರ್ವೇ" : "Survey"}
            </TabsTrigger>
          </TabsList>

          {/* Content for all roles uses same layout */}
          {["farmer", "revenue", "survey"].map((role) => (
            <TabsContent key={role} value={role} className="mt-4">
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                {/* Sidebar nav */}
                <div className="lg:col-span-1">
                  <Card className="p-3 sticky top-4 border-stone-200" data-testid="manual-sidebar">
                    <h3 className="text-xs font-semibold text-stone-400 uppercase tracking-wider px-2 mb-2">
                      {lang === "kn" ? "ವಿಷಯಗಳು" : "Contents"}
                    </h3>
                    <nav className="space-y-0.5">
                      {manual?.sections?.map((section) => {
                        const Icon = sectionIcons[section.id] || FileText;
                        return (
                          <button
                            key={section.id}
                            onClick={() => setActiveSection(section.id)}
                            data-testid={`nav-${section.id}`}
                            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                              activeSection === section.id
                                ? "bg-emerald-50 text-emerald-800 font-medium"
                                : "text-stone-600 hover:bg-stone-50 hover:text-stone-900"
                            }`}
                          >
                            <Icon className="w-4 h-4 flex-shrink-0" />
                            <span className="truncate">{t(section.title)}</span>
                          </button>
                        );
                      })}
                      {/* Workflow diagrams nav item */}
                      <button
                        onClick={() => setActiveSection("__workflows__")}
                        data-testid="nav-workflows"
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                          activeSection === "__workflows__"
                            ? "bg-emerald-50 text-emerald-800 font-medium"
                            : "text-stone-600 hover:bg-stone-50 hover:text-stone-900"
                        }`}
                      >
                        <ClipboardList className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate">
                          {lang === "kn" ? "ಕಾರ್ಯಪ್ರವಾಹ ರೇಖಾಚಿತ್ರಗಳು" : "Workflow Diagrams"}
                        </span>
                      </button>
                    </nav>
                  </Card>
                </div>

                {/* Main content */}
                <div className="lg:col-span-3 space-y-4">
                  {/* Manual Title */}
                  <Card className="p-5 border-stone-200 bg-gradient-to-r from-emerald-50 to-white" data-testid="manual-header-card">
                    <h2 className="text-lg sm:text-xl font-bold text-emerald-800">
                      {manual ? t(manual.title) : ""}
                    </h2>
                    <p className="text-stone-500 text-sm mt-1">
                      {manual ? t(manual.subtitle) : ""}
                    </p>
                  </Card>

                  {/* Section Content */}
                  {activeSection === "__workflows__" ? (
                    <WorkflowsSection
                      workflows={workflows}
                      activeRole={activeRole}
                      lang={lang}
                      t={t}
                    />
                  ) : currentSection ? (
                    <SectionContent section={currentSection} lang={lang} t={t} />
                  ) : (
                    <Card className="p-8 text-center text-stone-400 border-stone-200">
                      <BookOpen className="w-12 h-12 mx-auto mb-3 text-stone-300" />
                      <p>
                        {lang === "kn"
                          ? "ವಿಷಯವನ್ನು ವೀಕ್ಷಿಸಲು ಎಡಭಾಗದಲ್ಲಿ ವಿಭಾಗ ಆಯ್ಕೆಮಾಡಿ"
                          : "Select a section from the left to view content"}
                      </p>
                    </Card>
                  )}
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>

      {/* Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 mt-6">
        <div className="text-center text-xs text-stone-400">
          {lang === "kn"
            ? "ಮಾಗಡಿ ಅದಾಲತ್ ಪೋರ್ಟಲ್ - ರೈತರ ಅರ್ಜಿ ಟ್ರ್ಯಾಕಿಂಗ್ ವ್ಯವಸ್ಥೆ"
            : "Magadi Aadalat Portal - Farmer Application Tracking System"}
        </div>
      </div>
    </div>
  );
}

// ─── Section Content Component ───
function SectionContent({ section, lang, t }) {
  const Icon = sectionIcons[section.id] || FileText;

  return (
    <Card className="border-stone-200 overflow-hidden" data-testid={`section-${section.id}`}>
      {/* Section Header */}
      <div className="bg-emerald-50 border-b border-emerald-100 px-5 py-4 flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-emerald-600 flex items-center justify-center">
          <Icon className="w-5 h-5 text-white" />
        </div>
        <h3 className="text-base sm:text-lg font-bold text-emerald-900">{t(section.title)}</h3>
      </div>

      <div className="p-5 space-y-5">
        {/* Content paragraph */}
        {section.content && (
          <p className="text-stone-700 text-sm leading-relaxed">{t(section.content)}</p>
        )}

        {/* Steps */}
        {section.steps && (
          <div className="space-y-3">
            {section.steps.map((step, idx) => (
              <div
                key={idx}
                className="flex gap-3 items-start bg-stone-50 rounded-lg p-4 border border-stone-100"
              >
                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs font-bold">
                  {idx + 1}
                </div>
                <div>
                  <h4 className="font-semibold text-stone-900 text-sm">{t(step.title)}</h4>
                  <p className="text-stone-600 text-sm mt-1 leading-relaxed">{t(step.description)}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Features list */}
        {section.features && (
          <div className="space-y-2">
            {section.features.map((feature, idx) => (
              <div key={idx} className="flex items-start gap-2.5 text-sm text-stone-700">
                <ChevronRight className="w-4 h-4 mt-0.5 text-emerald-500 flex-shrink-0" />
                <span>{t(feature)}</span>
              </div>
            ))}
          </div>
        )}

        {/* Hierarchy */}
        {section.hierarchy && (
          <div className="space-y-2">
            {section.hierarchy.map((role, idx) => (
              <div
                key={idx}
                className="flex items-center gap-2"
                style={{ paddingLeft: `${(role.level - 1) * 24}px` }}
              >
                <Badge
                  variant="outline"
                  className={`text-xs ${
                    role.level === 1
                      ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                      : role.level === 2
                      ? "bg-blue-50 text-blue-700 border-blue-200"
                      : role.level === 3
                      ? "bg-amber-50 text-amber-700 border-amber-200"
                      : "bg-stone-50 text-stone-600 border-stone-200"
                  }`}
                >
                  L{role.level}
                </Badge>
                <span className="text-sm font-medium text-stone-800">{t(role)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

// ─── Workflows Section ───
function WorkflowsSection({ workflows, activeRole, lang, t }) {
  if (!workflows) return null;

  const workflowKeys =
    activeRole === "farmer"
      ? ["revenue", "survey", "both"]
      : activeRole === "revenue"
      ? ["revenue", "both"]
      : ["survey", "both"];

  return (
    <div className="space-y-6" data-testid="workflows-section">
      {/* Workflow diagrams */}
      {workflowKeys.map((key) => {
        const wf = workflows[key];
        if (!wf) return null;
        const def = buildWorkflowDef(wf.steps, t);
        return (
          <Card key={key} className="border-stone-200 overflow-hidden" data-testid={`workflow-${key}`}>
            <div className="bg-emerald-50 border-b border-emerald-100 px-5 py-4 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-emerald-600 flex items-center justify-center">
                <ClipboardList className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-base font-bold text-emerald-900">{t(wf.title)}</h3>
                <p className="text-xs text-stone-500">
                  {wf.step_count} {lang === "kn" ? "ಹಂತಗಳು" : "steps"}
                </p>
              </div>
              {key === "both" && (
                <div className="ml-auto flex gap-2">
                  <Badge className="bg-emerald-100 text-emerald-800 text-xs">
                    {lang === "kn" ? "ಕಂದಾಯ" : "Revenue"}
                  </Badge>
                  <Badge className="bg-sky-100 text-sky-800 text-xs">
                    {lang === "kn" ? "ಸರ್ವೇ" : "Survey"}
                  </Badge>
                </div>
              )}
            </div>
            <div className="p-5">
              <MermaidDiagram definition={def} diagramKey={`wf-${key}-${lang}`} />
            </div>
          </Card>
        );
      })}

      {/* Hierarchy diagrams */}
      {(activeRole === "revenue" || activeRole === "farmer") && workflows.hierarchy?.revenue && (
        <Card className="border-stone-200 overflow-hidden" data-testid="hierarchy-revenue">
          <div className="bg-emerald-50 border-b border-emerald-100 px-5 py-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-600 flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-base font-bold text-emerald-900">
              {lang === "kn" ? "ಕಂದಾಯ ಇಲಾಖೆ ಶ್ರೇಣಿ" : "Revenue Department Hierarchy"}
            </h3>
          </div>
          <div className="p-5">
            <MermaidDiagram
              definition={buildHierarchyDef(workflows.hierarchy.revenue.roles, t)}
              diagramKey={`hier-revenue-${lang}`}
            />
          </div>
        </Card>
      )}

      {(activeRole === "survey" || activeRole === "farmer") && workflows.hierarchy?.survey && (
        <Card className="border-stone-200 overflow-hidden" data-testid="hierarchy-survey">
          <div className="bg-emerald-50 border-b border-emerald-100 px-5 py-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-600 flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <h3 className="text-base font-bold text-emerald-900">
              {lang === "kn" ? "ಸರ್ವೇ ಇಲಾಖೆ ಶ್ರೇಣಿ" : "Survey Department Hierarchy"}
            </h3>
          </div>
          <div className="p-5">
            <MermaidDiagram
              definition={buildHierarchyDef(workflows.hierarchy.survey.roles, t)}
              diagramKey={`hier-survey-${lang}`}
            />
          </div>
        </Card>
      )}
    </div>
  );
}
